import { Injectable } from '@angular/core'
import { firstValueFrom, map, Observable, switchMap, tap } from 'rxjs'

import { automationFormDefaults } from '@employer/app/components/jobs-automation/automation-form.model'
import { environment } from '@employer/environments/environment'
import { selectors } from '@employer/app/store/selectors'
import { RestApiClient } from '@engineering11/web-api-rest'
import { isNotNil } from '@engineering11/utility'
import { E11Logger } from '@engineering11/web-api-error'
import { Store } from '@ngrx/store'
import { filter } from 'rxjs/operators'
import { DEFAULT_NETWORK_MONITORING_CONFIG, getBaseSuffix, TokenStorage } from 'shared-lib'
import { ICompanyAutomationFormData, JobPostAutomationCompanyRepository } from './job-post-automation-company.repository'

// Editing the company automation config requires the edit-automation-message entitlement (Cnect Plus).
// The write is enforced by the gated backend endpoint; the client-side Firestore write is closed by a
// security rule. Reads stay client-side (repository) so Default accounts can still view it read-only.
const COMPANY_CONFIG_BASE = 'jobs-automation/company-config'

@Injectable({
  providedIn: 'root',
})
export class JobsPostAutomationCompanySettingsService {
  private restClient: RestApiClient

  constructor(
    private store: Store,
    private jobPostAutomationCustomerRepository: JobPostAutomationCompanyRepository,
    private logger: E11Logger,
    tokenStorage: TokenStorage
  ) {
    this.restClient = new RestApiClient({
      baseUrl: environment.services.jobsAutomation,
      token: () => tokenStorage.getAccessToken(),
      baseSuffix: getBaseSuffix(environment, 'jobsAutomation'),
      ...DEFAULT_NETWORK_MONITORING_CONFIG,
    })
  }

  private getCustomerAutomationConfig(customerKey: string): Observable<ICompanyAutomationFormData> {
    return this.jobPostAutomationCustomerRepository.get(customerKey).pipe(
      tap(a => this.logger.log('getAutomationForCustomer: ', a)),
      map(res => (res ? res : automationFormDefaults))
    )
  }

  getCurrentCustomerAutomationConfig(): Observable<ICompanyAutomationFormData> {
    return this.store.select(selectors.getCustomerKey).pipe(
      filter(isNotNil),
      tap(customerKey => this.logger.log('Get automation config for: ', customerKey)),
      switchMap(customerKey => this.getCustomerAutomationConfig(customerKey as string))
    )
  }

  async setAutomation(formData: Partial<ICompanyAutomationFormData>): Promise<ICompanyAutomationFormData> {
    this.logger.log('JobPostAutomationCompanySettingsService.setAutomation', formData)

    const config: ICompanyAutomationFormData = { ...automationFormDefaults, ...formData }
    // customerKey is derived server-side from the auth token (@Claim); the body id is ignored.
    await firstValueFrom(this.restClient.put(COMPANY_CONFIG_BASE, config))
    this.logger.log('JobPostAutomationCompanySettingsService.setAutomation complete')
    return config
  }
}
